import { useLocalization } from '@/hooks/use-localization';
import { validators } from '@/hooks/use-validation';
import { Booleanified } from '../types';

type ValidatorType = keyof typeof validators;

const dictionary: Record<string, ValidatorType> = {
    socialSecurityNumber: 'socialSecurityNumber',
    governmentId: 'socialSecurityNumber',
    email: 'email',
    phone: 'swedishPhone',
    mobilePhoneNumber: 'swedishPhone',
    phoneNumber: 'swedishPhone',
    companyNumber: 'companyRegistrationNumber',
    organizationNumber: 'companyRegistrationNumber',
    postCode: 'postcode',
    zipCode: 'postcode',
    accountNumber: 'bankAccountAccountNumber',
    clearingNumber: 'bankAccountClearingNumber',
};

type DictionaryType = typeof dictionary;

export const useFieldValidation = () => {
    const { getLanguage } = useLocalization();

    const getValidatorFromFieldName = (key: keyof DictionaryType) => dictionary[key];

    const validate = (fieldName: string, value: string) => {
        if (fieldName in dictionary) {
            const validatorType = getValidatorFromFieldName(fieldName);
            return validators[validatorType](value, getLanguage());
        }

        return !!value;
    };

    function validateObject<T extends Record<string, string | Record<string, string>>>(objectToValidate: T): Booleanified<T> {
        const validationResults = {} as Booleanified<T>;

        for (const [key, value] of Object.entries(objectToValidate)) {
            if (typeof value === 'string') {
                validationResults[key as keyof T] = validate(key, value) as Booleanified<T>[keyof T];
                continue;
            }

            if (typeof value === 'object' && value !== null) {
                const nestedResult = {} as Record<string, boolean>;

                for (const [nestedKey, nestedValue] of Object.entries(value)) {
                    if (key === 'governmentId' && nestedKey === 'id') {
                        nestedResult[nestedKey] = true;
                        continue;
                    }
                    nestedResult[nestedKey] = validate(nestedKey, String(nestedValue));
                }

                validationResults[key as keyof T] = nestedResult as Booleanified<T>[keyof T];
                continue;
            }

            validationResults[key as keyof T] = false as Booleanified<T>[keyof T];
        }

        return validationResults;
    }

    const allFieldsValid = (fields: Record<string | number, boolean | Record<string | number, boolean>>) => {
        return Object.values(fields).every(field => (typeof field === 'boolean' ? field : Object.values(field).every(Boolean)));
    };

    return {
        validate,
        validateObject,
        allFieldsValid,
    };
};
